import { api } from '../api';
import { initAdminShell, escapeHtml, showError, hideError, setButtonBusy } from './shell';
import { t } from '../i18n/i18n';

interface Setting {
  id: number;
  name: string;
  value: string;
}

(async () => {
  try {
    await initAdminShell('app-settings');
  } catch {
    return;
  }

  const tbody = document.getElementById('settings-tbody');
  if (!tbody) return;

  async function load(): Promise<void> {
    hideError('settings-error');
    try {
      const client = await api();
      const { data } = await client.get<Setting[]>('/api/app-settings');
      render(data);
    } catch (err) {
      showError('settings-error', err instanceof Error ? err.message : 'Failed to load settings.');
    }
  }

  function render(settings: Setting[]): void {
    if (!tbody) return;
    if (settings.length === 0) {
      tbody.innerHTML = `<tr><td colspan="3" class="req-empty">${escapeHtml(t('admin.app_settings.empty'))}</td></tr>`;
      return;
    }
    // Settings are seeded by code; admins can only edit values, not add or delete.
    const saveLabel = t('admin.actions.save');
    tbody.innerHTML = settings.map((s) => `
      <tr data-id="${s.id}">
        <td>${escapeHtml(s.name)}</td>
        <td><input type="text" class="setting-value" value="${escapeHtml(s.value)}" required maxlength="2048" /></td>
        <td class="row-actions">
          <button type="button" class="row-action btn-save" data-id="${s.id}">${escapeHtml(saveLabel)}</button>
        </td>
      </tr>
    `).join('');

    tbody.querySelectorAll<HTMLButtonElement>('button.btn-save').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = Number(btn.dataset.id);
        const row = btn.closest('tr');
        const input = row?.querySelector<HTMLInputElement>('input.setting-value');
        if (!input) return;
        // Backend requires a non-empty value; guard here since this saves on a
        // button click (no form submit to enforce the `required` attribute).
        if (!input.value.trim()) {
          showError('settings-error', 'Value cannot be empty.');
          input.focus();
          return;
        }
        setButtonBusy(btn, true);
        try {
          const client = await api();
          await client.put(`/api/app-settings/${id}`, { value: input.value });
          const original = btn.textContent;
          btn.textContent = '✓';
          setTimeout(() => { btn.textContent = original; }, 1500);
        } catch (err) {
          showError('settings-error', err instanceof Error ? err.message : 'Save failed.');
        } finally {
          setButtonBusy(btn, false);
        }
      });
    });
  }

  await load();
})();
